// «نوع الوحدة» — the shape-4 unit-type editor in the 420 px SidePanel
// (HANDOFF §1; board 25 v2 shape--4 + board 24 second half). Fields map to
// booking's unit-type JSON (Phase 1A Task 11): name, quantity (1..500),
// min_nights, photos (≤ 10, each uploaded to profile first — https only),
// price_unit night|day + price_iqd, weekend_price_iqd (sub = the business's
// weekend nights), seasons (dated rows, ≤ 20, PUT after the type is saved),
// day use (from, to, price — all three when on), the deposit (percent |
// fixed_per_night) and the type's own check-in/out (empty = the business
// default). The server's 422 sentences land on their fields. «حفظ» is the
// panel's coral; the wizard's is behind it. A seasons failure keeps the panel
// open on the seasons; the type itself stays saved (the retry only PUTs).

import { useMemo, useState, type ReactNode } from 'react';
import { ImagePlus, Plus, X } from 'lucide-react';
import { SidePanel } from '@/app/SidePanel';
import { PrimaryAction } from '@/components/Button';
import { Chip, Toggle } from '@/components/Form';
import { isApiError } from '@/lib/api';
import { normalizeDigits } from '@/lib/digits';
import { Msg } from '../../components/widgets';
import { ERRORS, UNIT } from '../../copy';
import { createUnitType, patchUnitType, putSeasons, uploadUnitPhoto, type PriceUnit, type UnitDepositKind, type UnitType, type UnitTypeBody, type UnitTypeSeason } from '../../data/shapeApi';
import { hhmm } from '../../preview';
import { toMinutes, WEEK } from '../../state';

interface SeasonDraft {
  from: string;
  to: string;
  price: string;
}

interface Form {
  name: string;
  description: string;
  quantity: string;
  minNights: string;
  photos: string[];
  priceUnit: PriceUnit;
  price: string;
  weekend: string;
  seasons: SeasonDraft[];
  dayUse: boolean;
  dayFrom: string;
  dayTo: string;
  dayPrice: string;
  depositKind: UnitDepositKind | null;
  depositValue: string;
  checkIn: string;
  checkOut: string;
}

const str = (n: number | undefined | null) => (n == null ? '' : String(n));
const time = (n: number | undefined | null) => (n == null ? '' : hhmm(n));

function formOf(t: UnitType | null): Form {
  return {
    name: t?.name ?? '',
    description: t?.description ?? '',
    quantity: t ? String(t.quantity) : '1',
    minNights: t ? String(t.min_nights) : '1',
    photos: t?.photos ?? [],
    priceUnit: t?.price_unit ?? 'night',
    price: str(t?.price_iqd),
    weekend: str(t?.weekend_price_iqd),
    seasons: (t?.seasons ?? []).map((s) => ({ from: s.from_date, to: s.to_date, price: String(s.price_iqd) })),
    dayUse: t?.day_use_enabled ?? false,
    dayFrom: time(t?.day_use_from_min),
    dayTo: time(t?.day_use_to_min),
    dayPrice: str(t?.day_use_price_iqd),
    depositKind: t?.deposit_kind ?? null,
    depositValue: str(t?.deposit_value),
    checkIn: time(t?.check_in_min),
    checkOut: time(t?.check_out_min),
  };
}

const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(normalizeDigits(v)));

/** The wire body: on a create the empty optionals are left out; on a PATCH they are cleared (null). */
function bodyOf(f: Form, create: boolean, photos: string[]): UnitTypeBody {
  const opt = <T,>(v: T | undefined): T | null | undefined => (v === undefined ? (create ? undefined : null) : v);
  const b: UnitTypeBody = {
    name: f.name.trim(),
    quantity: num(f.quantity),
    min_nights: num(f.minNights) ?? 1,
    photos,
    price_unit: f.priceUnit,
    // «وصف قصير» (H5): a create leaves an empty one out; a PATCH sends it as typed — "" clears it on the server
    description: f.description.trim() === '' && create ? undefined : f.description.trim(),
    price_iqd: num(f.price),
    weekend_price_iqd: opt(num(f.weekend)),
    day_use_enabled: f.dayUse,
    check_in_min: opt(toMinutes(f.checkIn) ?? undefined),
    check_out_min: opt(toMinutes(f.checkOut) ?? undefined),
  };
  if (f.dayUse) {
    b.day_use_from_min = toMinutes(f.dayFrom) ?? undefined;
    b.day_use_to_min = toMinutes(f.dayTo) ?? undefined;
    b.day_use_price_iqd = num(f.dayPrice);
  } else if (!create) {
    b.day_use_from_min = null;
    b.day_use_to_min = null;
    b.day_use_price_iqd = null;
  }
  if (f.depositKind) {
    b.deposit_kind = f.depositKind;
    b.deposit_value = opt(num(f.depositValue));
  } else if (!create) {
    b.deposit_kind = null;
    b.deposit_value = null;
  }
  for (const k of Object.keys(b) as Array<keyof UnitTypeBody>) if (b[k] === undefined) delete b[k];
  return b;
}

function seasonsOf(f: Form): UnitTypeSeason[] {
  return f.seasons.map((s) => ({ from_date: s.from.trim(), to_date: s.to.trim(), price_iqd: num(s.price) ?? 0 }));
}

/** «الخميس والجمعة» from booking weekday numbers (0 = Sunday). */
export function weekendWords(nights: number[]): string {
  const names = WEEK.filter((d) => nights.includes(d.weekday)).map((d) => d.name);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join('، ')} و${names[names.length - 1]}`;
}

/** The 422 row's field → the panel's field key (photos[2] → photos, seasons[0].to_date → seasons). */
const fieldKey = (f: string) => f.replace(/\[\d+\].*$/, '');

function messageOf(e: unknown): string {
  if (isApiError(e)) return e.errors?.find((r) => r.message)?.message ?? e.detail ?? (e.status >= 500 ? ERRORS.server : e.message);
  return ERRORS.network;
}

export interface UnitTypePanelProps {
  businessId: string;
  /** null = a new type. */
  editing: UnitType | null;
  weekendNights: number[];
  onClose: () => void;
  onSaved: (t: UnitType) => void;
}

export function UnitTypePanel({ businessId, editing, weekendNights, onClose, onSaved }: UnitTypePanelProps) {
  const [form, setForm] = useState<Form>(() => formOf(editing));
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The type as saved in this panel (a create that went through keeps its id; the retry only PUTs the seasons). */
  const [saved, setSaved] = useState<{ type: UnitType; body: string } | null>(editing ? { type: editing, body: '' } : null);
  const weekend = useMemo(() => weekendWords(weekendNights), [weekendNights]);

  const set = (patch: Partial<Form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
    setGeneral(null);
  };
  const setSeason = (i: number, patch: Partial<SeasonDraft>) => set({ seasons: form.seasons.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  const save = async () => {
    if (busy) return;
    // Day use: from, to and price together (booking's rule, its sentences).
    if (form.dayUse) {
      const e: Record<string, string> = {};
      if (toMinutes(form.dayFrom) == null) e.day_use_from_min = 'أدخل بداية الاستخدام النهاري';
      if (toMinutes(form.dayTo) == null) e.day_use_to_min = 'أدخل نهاية الاستخدام النهاري';
      if (num(form.dayPrice) == null) e.day_use_price_iqd = 'أدخل سعر الاستخدام النهاري';
      if (Object.keys(e).length) {
        setErrors(e);
        return;
      }
    }
    setBusy(true);
    setErrors({});
    setGeneral(null);
    try {
      // 1. photos first: a failed upload blocks the save.
      // each uploaded URL is kept at once: a retry uploads only what is still pending
      let photos = form.photos;
      const pending = [...files];
      while (pending.length) {
        try {
          const url = await uploadUnitPhoto(businessId, pending[0]);
          photos = [...photos, url];
          pending.shift();
          setForm((f) => ({ ...f, photos }));
          setFiles([...pending]);
        } catch (e) {
          setErrors({ photos: messageOf(e) });
          return;
        }
      }
      // 2. the type (create, or PATCH when anything changed since this panel saved it).
      const create = !saved;
      const body = bodyOf({ ...form, photos }, create, photos);
      const bodyJson = JSON.stringify(body);
      let type = saved?.type ?? null;
      if (!saved || saved.body !== bodyJson) {
        try {
          type = create ? await createUnitType(businessId, body) : await patchUnitType(businessId, saved!.type.id, body);
          setSaved({ type, body: bodyJson });
        } catch (e) {
          if (isApiError(e) && e.status === 422 && e.errors?.length) {
            const out: Record<string, string> = {};
            for (const r of e.errors) if (r.field && !out[fieldKey(r.field)]) out[fieldKey(r.field)] = r.message ?? '';
            setErrors(out);
          } else setGeneral(messageOf(e));
          return;
        }
      }
      // 3. the seasons (replace-set) once the type exists.
      const seasons = seasonsOf(form);
      const before = JSON.stringify((editing?.seasons ?? []).map(({ from_date, to_date, price_iqd }) => ({ from_date, to_date, price_iqd })));
      if (type && (create ? seasons.length > 0 : JSON.stringify(seasons) !== before)) {
        try {
          const res = await putSeasons(businessId, type.id, seasons);
          type = { ...type, seasons: res };
        } catch (e) {
          setErrors({ seasons: messageOf(e) });
          onSaved(type);
          return;
        }
      }
      if (type) onSaved(type);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) =>
    errors[k] ? (
      <Msg testId={`onb-ut-err-${k}`}>{errors[k]}</Msg>
    ) : null;
  const field = (label: ReactNode, control: ReactNode, k: string, hint?: ReactNode) => (
    <div className="onb-ut-field">
      <span className="onb-ut-label">{label}</span>
      {hint && <span className="onb-row-sub">{hint}</span>}
      {control}
      {err(k)}
    </div>
  );
  const input = (testId: string, value: string, onChange: (v: string) => void, opts: { numeric?: boolean; type?: string; label: string; maxLength?: number }) => (
    <input
      className={opts.numeric || opts.type ? 'onb-entity-price ltr' : 'onb-entity-name'}
      dir={opts.numeric || opts.type ? 'ltr' : undefined}
      type={opts.type ?? 'text'}
      inputMode={opts.numeric ? 'numeric' : undefined}
      maxLength={opts.maxLength}
      aria-label={opts.label}
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(opts.numeric ? normalizeDigits(e.target.value) : e.target.value)}
    />
  );

  return (
    <SidePanel
      open
      title={editing?.name || UNIT.panelTitle}
      onClose={onClose}
      data-testid="onb-ut-panel"
      footer={
        <PrimaryAction size="lg" block loading={busy} onClick={() => void save()}>
          {UNIT.save}
        </PrimaryAction>
      }
    >
      <div className="onb-ut-form">
        {general && <Msg testId="onb-ut-general">{general}</Msg>}
        {field(UNIT.name, input('onb-ut-name', form.name, (v) => set({ name: v }), { label: UNIT.name }), 'name')}
        {field(UNIT.description, input('onb-ut-description', form.description, (v) => set({ description: v }), { label: UNIT.description, maxLength: 120 }), 'description', UNIT.descriptionHint)}
        {field(UNIT.quantity, input('onb-ut-quantity', form.quantity, (v) => set({ quantity: v }), { numeric: true, label: UNIT.quantity }), 'quantity')}
        {field(UNIT.minNights, input('onb-ut-min', form.minNights, (v) => set({ minNights: v }), { numeric: true, label: UNIT.minNights }), 'min_nights')}
        {field(
          UNIT.photos,
          <div className="onb-ut-photos">
            {form.photos.map((u) => (
              <img key={u} src={u} alt="" className="onb-ut-photo" />
            ))}
            {files.map((f) => (
              <span key={f.name} className="onb-pill" data-tone="warn">
                {f.name}
              </span>
            ))}
            <label className="onb-entity-add onb-ut-photo-add">
              <ImagePlus aria-hidden="true" />
              {UNIT.photos}
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                data-testid="onb-ut-photos"
                onChange={(e) => {
                  const picked = Array.from(e.target.files ?? []);
                  setFiles((xs) => [...xs, ...picked].slice(0, Math.max(0, 10 - form.photos.length)));
                  setErrors({});
                }}
              />
            </label>
          </div>,
          'photos',
        )}
        {field(
          UNIT.priceUnit,
          <div className="onb-sitting-opts">
            <Chip selected={form.priceUnit === 'night'} onClick={() => set({ priceUnit: 'night' })}>
              {UNIT.perNight}
            </Chip>
            <Chip selected={form.priceUnit === 'day'} onClick={() => set({ priceUnit: 'day' })}>
              {UNIT.perDay}
            </Chip>
          </div>,
          'price_unit',
        )}
        {field(form.priceUnit === 'day' ? UNIT.priceDay : UNIT.priceNight, input('onb-ut-price', form.price, (v) => set({ price: v }), { numeric: true, label: UNIT.priceNight }), 'price_iqd')}
        {field(UNIT.weekend, input('onb-ut-weekend', form.weekend, (v) => set({ weekend: v }), { numeric: true, label: UNIT.weekend }), 'weekend_price_iqd', weekend)}
        {field(
          UNIT.seasons,
          <div className="onb-ut-seasons">
            {form.seasons.map((s, i) => (
              <div key={i} className="onb-row onb-band-row">
                {input(`onb-ut-season-from-${i}`, s.from, (v) => setSeason(i, { from: v }), { type: 'date', label: 'من' })}
                {input(`onb-ut-season-to-${i}`, s.to, (v) => setSeason(i, { to: v }), { type: 'date', label: 'إلى' })}
                {input(`onb-ut-season-price-${i}`, s.price, (v) => setSeason(i, { price: v }), { numeric: true, label: UNIT.priceNight })}
                <button type="button" className="onb-entity-remove" aria-label="حذف" onClick={() => set({ seasons: form.seasons.filter((_, j) => j !== i) })}>
                  <X aria-hidden="true" />
                </button>
              </div>
            ))}
            {form.seasons.length < 20 && (
              <button type="button" className="onb-entity-add" data-testid="onb-ut-season-add" onClick={() => set({ seasons: [...form.seasons, { from: '', to: '', price: '' }] })}>
                <Plus aria-hidden="true" />
                {UNIT.seasonAdd}
              </button>
            )}
          </div>,
          'seasons',
        )}
        <div className="onb-ut-field">
          <Toggle checked={form.dayUse} onChange={(dayUse) => set({ dayUse })} label={UNIT.dayUse} description={UNIT.dayUseSub} />
          {form.dayUse && (
            <div className="onb-row onb-band-row">
              {input('onb-ut-dayuse-from', form.dayFrom, (v) => set({ dayFrom: v }), { type: 'time', label: 'من' })}
              {input('onb-ut-dayuse-to', form.dayTo, (v) => set({ dayTo: v }), { type: 'time', label: 'إلى' })}
              {input('onb-ut-dayuse-price', form.dayPrice, (v) => set({ dayPrice: v }), { numeric: true, label: UNIT.dayUse })}
            </div>
          )}
          {err('day_use_from_min')}
          {err('day_use_to_min')}
          {err('day_use_price_iqd')}
        </div>
        {field(
          UNIT.deposit,
          <>
            <div className="onb-sitting-opts">
              <Chip selected={form.depositKind === 'percent'} onClick={() => set({ depositKind: form.depositKind === 'percent' ? null : 'percent' })}>
                {UNIT.depositPercent}
              </Chip>
              <Chip selected={form.depositKind === 'fixed_per_night'} onClick={() => set({ depositKind: form.depositKind === 'fixed_per_night' ? null : 'fixed_per_night' })}>
                {UNIT.depositFixed}
              </Chip>
            </div>
            {form.depositKind && input('onb-ut-deposit-value', form.depositValue, (v) => set({ depositValue: v }), { numeric: true, label: UNIT.deposit })}
            {err('deposit_value')}
          </>,
          'deposit_kind',
        )}
        {field(UNIT.checkIn, input('onb-ut-check-in', form.checkIn, (v) => set({ checkIn: v }), { type: 'time', label: UNIT.checkIn }), 'check_in_min')}
        {field(UNIT.checkOut, input('onb-ut-check-out', form.checkOut, (v) => set({ checkOut: v }), { type: 'time', label: UNIT.checkOut }), 'check_out_min')}
      </div>
    </SidePanel>
  );
}
